import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { eligibleGames, type RediscoverCollection } from './rediscover';
import type {
  CollectionSummary,
  GameCopy,
  GameRecord,
  MediaType,
  PlatformSummary,
} from '@pixel-archive/shared';
import {
  compareMachinePlatforms,
  gameBelongsToPlatform,
  isVisibleMachinePlatform,
  machineCompatibilityName,
  type CompatiblePlatform,
} from './compatibility';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
export const configured = Boolean(url && key && !url.includes('votre-projet'));
export const supabase: SupabaseClient | null = configured
  ? createClient(url!, key!, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  : null;

type ViewerGameRow = {
  id: string;
  title: string;
  normalized_title: string;
  platform_id: string;
  platform_name: string;
  publisher: string | null;
  release_year: number | null;
  cover_url: string | null;
  status: 'owned' | 'wishlist' | 'missing';
  copy_count: number;
  imported_at: string | null;
  compatible_platforms: string | null;
};
const mapGame = (r: ViewerGameRow): GameRecord => ({
  id: r.id,
  title: r.title,
  normalizedTitle: r.normalized_title,
  platformId: r.platform_id,
  platformName: r.platform_name,
  publisher: r.publisher,
  releaseYear: r.release_year,
  coverUrl: r.cover_url,
  status: r.status,
  copyCount: r.copy_count,
  importedAt: r.imported_at ?? undefined,
});

export type ViewerHardware = {
  id: string;
  name: string;
  category: string | null;
  platformId: string | null;
  coverUrl: string | null;
  quantity: number;
  notes: string | null;
};

export type SearchSuggestion =
  | { kind: 'game'; id: string; label: string; detail: string }
  | { kind: 'hardware'; id: string; platformId: string | null; label: string; detail: string };

const mapHardware = (x: Record<string, unknown>): ViewerHardware => ({
  id: String(x.id),
  name: String(x.name),
  category: x.category == null ? null : String(x.category),
  platformId: x.platform_id == null ? null : String(x.platform_id),
  coverUrl: x.cover_path == null ? null : String(x.cover_path),
  quantity: Math.max(1, Number(x.quantity) || 1),
  notes: x.notes == null ? null : String(x.notes),
});
const client = (): SupabaseClient => {
  if (!supabase) throw new Error('Le viewer en ligne n’est pas encore relié à votre espace privé.');
  return supabase;
};

// Ce module n’expose volontairement aucune fonction INSERT, UPDATE ou DELETE.
export async function getRediscoverCollection(): Promise<RediscoverCollection> {
  const rows: ViewerGameRow[] = [];
  const platforms: CompatiblePlatform[] = [];
  const pageSize = 1000;
  // Load once per page visit, through the existing authenticated, RLS-protected views.
  await Promise.all([
    (async () => {
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await client()
          .from('viewer_games')
          .select('*')
          .eq('status', 'owned')
          .order('id')
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const page = (data ?? []) as ViewerGameRow[];
        rows.push(...page);
        if (page.length < pageSize) break;
      }
    })(),
    (async () => {
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await client()
          .from('viewer_platforms')
          .select('id,name,code')
          .order('id')
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const page = (data ?? []) as CompatiblePlatform[];
        platforms.push(...page);
        if (page.length < pageSize) break;
      }
    })(),
  ]);
  const games = eligibleGames(
    rows.map((row) => ({ ...mapGame(row), compatiblePlatforms: row.compatible_platforms })),
  );
  return {
    games,
    machines: platforms
      .filter((platform) => eligibleGames(games, platform).length > 0)
      .sort(compareMachinePlatforms),
  };
}

function cleanSearchQuery(query: string): string {
  return query.trim().replace(/[,%()]/g, '');
}

function gameSearchFilter(clean: string): string {
  return ['normalized_title', 'title', 'platform_name', 'publisher']
    .map((field) => `${field}.ilike.%${clean}%`)
    .join(',');
}

export async function searchCollection(query: string): Promise<GameRecord[]> {
  const clean = cleanSearchQuery(query);
  const rows: ViewerGameRow[] = [];
  const pageSize = 1000;
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client()
      .from('viewer_games')
      .select('*')
      // Keep the normalized-title match and also accept accents in the displayed title.
      // One OR query returns each game once, even when several fields match.
      .or(gameSearchFilter(clean))
      .order('title')
      .order('id')
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const page = (data ?? []) as ViewerGameRow[];
    rows.push(...page);
    if (page.length < pageSize) break;
  }
  return rows.map(mapGame);
}

export async function getSearchSuggestions(query: string): Promise<SearchSuggestion[]> {
  const clean = cleanSearchQuery(query);
  if (clean.length < 2) return [];
  const [gamesResult, hardwareResult] = await Promise.all([
    client()
      .from('viewer_games')
      .select('id,title,platform_name')
      .or(gameSearchFilter(clean))
      .order('title')
      .order('id')
      .limit(6),
    client()
      .from('viewer_hardware')
      .select('id,name,category,platform_id')
      .eq('status', 'owned')
      .ilike('name', `%${clean}%`)
      .order('name')
      .order('id')
      .limit(4),
  ]);
  if (gamesResult.error && hardwareResult.error) {
    throw gamesResult.error;
  }
  return [
    ...(gamesResult.data ?? []).map((x) => ({
      kind: 'game' as const,
      id: String(x.id),
      label: String(x.title),
      detail: String(x.platform_name),
    })),
    ...(hardwareResult.data ?? []).map((x) => ({
      kind: 'hardware' as const,
      id: String(x.id),
      platformId: x.platform_id == null ? null : String(x.platform_id),
      label: String(x.name),
      detail: x.category == null ? 'Matériel' : String(x.category),
    })),
  ].slice(0, 10);
}

export async function searchCollectionEntities(query: string): Promise<SearchSuggestion[]> {
  const clean = cleanSearchQuery(query);
  if (clean.length < 2) return [];
  const pageSize = 1000;
  const hardware: SearchSuggestion[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client()
      .from('viewer_hardware')
      .select('id,name,category,platform_id')
      .eq('status', 'owned')
      .ilike('name', `%${clean}%`)
      .order('name')
      .order('id')
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const page = data ?? [];
    hardware.push(
      ...page.map((x) => ({
        kind: 'hardware' as const,
        id: String(x.id),
        platformId: x.platform_id == null ? null : String(x.platform_id),
        label: String(x.name),
        detail: x.category == null ? 'Matériel' : String(x.category),
      })),
    );
    if (page.length < pageSize) break;
  }
  return hardware;
}
export async function getGame(id: string): Promise<GameRecord> {
  const { data, error } = await client().from('viewer_games').select('*').eq('id', id).single();
  if (error) throw error;
  return mapGame(data as ViewerGameRow);
}
export async function getGameCopies(id: string): Promise<GameCopy[]> {
  const current = await client()
    .from('viewer_copies')
    .select('id,region,edition,condition,has_box,has_manual,has_media,media_type,location,notes')
    .eq('game_id', id);
  let rows: Record<string, unknown>[] | null = current.data;
  let error = current.error;
  // Garde le Viewer utilisable pendant le déploiement progressif de la migration 0006.
  if (error) {
    const legacy = await client()
      .from('viewer_copies')
      .select('id,region,edition,condition,has_box,has_manual,has_media,location,notes')
      .eq('game_id', id);
    rows = legacy.data;
    error = legacy.error;
  }
  if (error) throw error;
  return (rows ?? []).map((x: Record<string, unknown>) => ({
    id: String(x.id),
    publisher: null,
    region: x.region as string | null,
    edition: x.edition as string | null,
    condition: x.condition as string | null,
    hasBox: x.has_box as boolean | null,
    hasManual: x.has_manual as boolean | null,
    hasMedia: x.has_media as boolean | null,
    mediaType: x.media_type as MediaType | null,
    location: x.location as string | null,
    notes: x.notes as string | null,
  }));
}
async function allPlatformRows<T>(
  fetchPage: (from: number, to: number) => PromiseLike<{ data: unknown[] | null; error: unknown }>,
): Promise<T[]> {
  const rows: T[] = [];
  const pageSize = 1000;
  for (let from = 0; ;) {
    const { data, error } = await fetchPage(from, from + pageSize - 1);
    if (error) throw error;
    const page = (data ?? []) as T[];
    rows.push(...page);
    // Advance by the actual count: also works if the project caps pages below 1000.
    if (!page.length) return rows;
    from += page.length;
  }
}

export async function getPlatforms(): Promise<PlatformSummary[]> {
  const [platforms, games, machines] = await Promise.all([
    allPlatformRows<Record<string, unknown>>((from, to) =>
      client().from('viewer_platforms').select('*').order('id').range(from, to),
    ),
    allPlatformRows<CompatibleGameRow>((from, to) =>
      client()
        .from('viewer_games')
        .select('platform_id,compatible_platforms')
        .eq('status', 'owned')
        .order('id')
        .range(from, to),
    ),
    allPlatformRows<{ platform_id: string | null; quantity: number | null }>((from, to) =>
      client()
        .from('viewer_hardware')
        .select('platform_id,quantity')
        .eq('status', 'owned')
        .eq('category', 'Machine / console')
        .order('id')
        .range(from, to),
    ),
  ]);
  const directCounts = new Map<string, number>();
  const machineCounts = new Map<string, number>();
  for (const game of games)
    directCounts.set(game.platform_id, (directCounts.get(game.platform_id) ?? 0) + 1);
  for (const machine of machines) {
    if (machine.platform_id)
      machineCounts.set(
        machine.platform_id,
        (machineCounts.get(machine.platform_id) ?? 0) + Math.max(1, Number(machine.quantity) || 1),
      );
  }
  return platforms
    .map((x: Record<string, unknown>) => {
      const platform: CompatiblePlatform = {
        id: String(x.id),
        name: String(x.name),
        code: String(x.code),
      };
      return {
        ...platform,
        ownedCount: machineCompatibilityName(platform)
          ? games.filter((game) => gameBelongsToPlatform(game, platform)).length
          : (directCounts.get(platform.id) ?? 0),
        directOwnedCount: directCounts.get(platform.id) ?? 0,
        catalogCount: x.catalog_count == null ? null : Number(x.catalog_count),
        machineCount: machineCounts.get(platform.id) ?? 0,
      };
    })
    .filter(isVisibleMachinePlatform)
    .sort(compareMachinePlatforms);
}

type CompatibleGameRow = Pick<ViewerGameRow, 'platform_id' | 'compatible_platforms'>;

export async function getPlatform(id: string): Promise<PlatformSummary> {
  const { data, error } = await client().from('viewer_platforms').select('*').eq('id', id).single();
  if (error) throw error;
  return {
    id: String(data.id),
    name: String(data.name),
    code: String(data.code),
    ownedCount: Number(data.owned_count),
    catalogCount: data.catalog_count == null ? null : Number(data.catalog_count),
    machineCount: Number(data.machine_count ?? 0),
  };
}

export async function getPlatformGames(id: string): Promise<GameRecord[]> {
  const platform = await getPlatform(id);
  const rows: ViewerGameRow[] = [];
  const pageSize = 1000;
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await client()
      .from('viewer_games')
      .select('*')
      .eq('status', 'owned')
      .order('title')
      .order('id')
      .range(from, from + pageSize - 1);
    if (error) throw error;
    const page = (data ?? []) as ViewerGameRow[];
    rows.push(...page);
    if (page.length < pageSize) break;
  }
  return rows.filter((game) => gameBelongsToPlatform(game, platform)).map(mapGame);
}

export async function getPlatformHardware(id: string): Promise<ViewerHardware[]> {
  const rows: Record<string, unknown>[] = [];
  const platformRows: Array<{ id: string; name: string; code: string }> = [];
  const pageSize = 1000;
  await Promise.all([
    (async () => {
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await client()
          .from('viewer_hardware')
          .select('id,name,category,platform_id,cover_path,quantity,notes')
          .eq('status', 'owned')
          .order('name')
          .order('id')
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const page = (data ?? []) as Record<string, unknown>[];
        rows.push(...page);
        if (page.length < pageSize) break;
      }
    })(),
    (async () => {
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await client()
          .from('viewer_platforms')
          .select('id,name,code')
          .order('name')
          .range(from, from + pageSize - 1);
        if (error) throw error;
        const page = (data ?? []) as Array<{ id: string; name: string; code: string }>;
        platformRows.push(...page);
        if (page.length < pageSize) break;
      }
    })(),
  ]);
  const normalize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fr')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const effectivePlatform = (item: ViewerHardware): string | null => {
    if (item.category === 'Machine / console') return item.platformId;
    const paddedName = ` ${normalize(item.name)} `;
    const matches = platformRows
      .map((platform) => {
        const code = normalize(platform.code);
        const name = normalize(platform.name);
        const score =
          code.length >= 3 && paddedName.includes(` ${code} `)
            ? 100 + code.length
            : name.length >= 4 && paddedName.includes(` ${name} `)
              ? 50 + name.length
              : 0;
        return { id: platform.id, score };
      })
      .filter((match) => match.score > 0)
      .sort((a, b) => b.score - a.score);
    return matches.length && (matches.length === 1 || matches[0].score > matches[1].score)
      ? matches[0].id
      : item.platformId;
  };
  return rows
    .map(mapHardware)
    .filter((item) => effectivePlatform(item) === id)
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}
export async function getCollectionSummary(): Promise<CollectionSummary> {
  const { data, error } = await client().from('viewer_collection_summary').select('*').single();
  if (error) throw error;
  return {
    games: Number(data.games),
    platforms: Number(data.platforms),
    machines: Number(data.machines),
    hardware: Number(data.hardware),
    wishlist: Number(data.wishlist),
    photos: Number(data.photos),
  };
}
export async function getWishlist(): Promise<GameRecord[]> {
  const rows = await allPlatformRows<ViewerGameRow>((from, to) =>
    client()
      .from('viewer_games')
      .select('*')
      .eq('status', 'wishlist')
      .order('title')
      .order('id')
      .range(from, to),
  );
  return rows.map(mapGame);
}
